import React, { type ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FooterActionBar,
  ModalIsland,
} from '@saviynt/design-system';
import { type Action, type Dispatch } from 'redux';

import { type Response, ResponseFailure } from '../../../models';
import {
  isEnumMember,
  type StateSetter,
  useInvertedLocalize,
} from '../../../utilities';
import * as api from '../api';
import { type FactorType, type ProtectedUrlMethods } from '../models';
import {
  type AppDispatch,
  privateActions,
  type RootState,
  thunks,
  useDispatch,
  useSelector,
} from '../store';

import EmailOtpForm from './EmailOtpForm';
import Menu from './Menu';
import { invertedMessages } from './messages';
import MfaError from './MfaError';
import SmsOtpForm from './SmsOtpForm';
import TotpForm from './TotpForm';

import './PromptModal.css';

const CODE_TOGGLE_DEFAULT = 'false' as const;

interface SelectedState {
  isOpen: boolean;
  onSuccessMessage?: Action;
  shouldSkipPrompt: boolean;
  requestedMethod: string;
  protectedUrls: Response<Record<ProtectedUrlMethods, string[]>>;
}

enum AlreadyVerifiedState {
  Unknown = 'Unknown',
  Pending = 'Pending',
  AlreadyVerified = 'AlreadyVerified',
  NotAlreadyVerified = 'NotAlreadyVerified',
}

/**
 * Allows users to verify their identity if MFA is enabled. To activate the
 * modal, dispatch a "verificationRequired" action. The action should include a
 * "callback action", which will be dispatched when the user successfully
 * verifies their identity.
 */
// eslint-disable-next-line import/prefer-default-export -- fix eslint warning about import/no-named-as-default
export function PromptModal() {
  const dispatch = useDispatch();
  const [selectedFactor, setSelectedFactor] = useState<FactorType | null>(null);
  const [alreadyVerifiedStatus, setAlreadyVerifiedStatus] =
    useState<AlreadyVerifiedState>(AlreadyVerifiedState.Unknown);
  const [canContinue, setCanContinue] = useState(false);
  const { isOpen, onSuccessMessage, shouldSkipPrompt, protectedUrls } =
    useSelector(selectState);
  const [isVerificationRequested, setIsVerificationRequested] = useState(false);
  const contentBody = useContentBody(
    selectedFactor,
    setSelectedFactor,
    setAlreadyVerifiedStatus,
    isVerificationRequested,
    setIsVerificationRequested,
    setCanContinue,
    protectedUrls,
    onSuccessMessage
  );
  const handleNextClick = () => setIsVerificationRequested(true);

  const handleBackClick = () => {
    setSelectedFactor(null);
    setCanContinue(false);
  };

  const handleClose = () => {
    setSelectedFactor(null);
    setAlreadyVerifiedStatus(AlreadyVerifiedState.Unknown);
    dispatch(privateActions.verificationDismissed());
    setCanContinue(false);
  };

  useMfaDisabledEffect(alreadyVerifiedStatus, setAlreadyVerifiedStatus);

  const { className, title, subTitle, footerActionBarComp } =
    useModalIslandProps(
      selectedFactor,
      handleNextClick,
      handleBackClick,
      canContinue
    );

  if (
    shouldSkipPrompt ||
    protectedUrls === undefined ||
    alreadyVerifiedStatus !== AlreadyVerifiedState.NotAlreadyVerified
  ) {
    return null;
  }

  return (
    <ModalIsland
      onClose={handleClose}
      isOpen={isOpen}
      className={className}
      title={title}
      subtitle={subTitle}
      contentBody={contentBody}
      FooterActionBarComp={footerActionBarComp}
    />
  );
}

// eslint-disable-next-line consistent-return -- final switch statement is exhaustive
function useContentBody(
  selectedFactor: FactorType | null,
  setSelectedFactor: StateSetter<FactorType | null>,
  setAlreadyVerifiedStatus: StateSetter<AlreadyVerifiedState>,
  isVerificationRequested: boolean,
  setIsVerificationRequested: StateSetter<boolean>,
  setCanContinue: StateSetter<boolean>,
  protectedUrls: Response<Record<ProtectedUrlMethods, string[]>>,
  onSuccessMessage?: Action
): ReactElement {
  const dispatch = useDispatch();
  const handleFailure = () => setIsVerificationRequested(false);
  const handleSubmittableChange = (state: boolean) => setCanContinue(state);

  const handleSuccess = () => {
    setSelectedFactor(null);
    setAlreadyVerifiedStatus(AlreadyVerifiedState.Unknown);
    setIsVerificationRequested(false);
    dispatchVerifications(dispatch, onSuccessMessage);
  };

  if (isEnumMember(protectedUrls, ResponseFailure)) {
    const handleRetry = () => {
      dispatch(thunks.requestProtectedUrls());
    };

    return <MfaError onRetry={handleRetry} />;
  }

  if (!selectedFactor) {
    return <Menu onSelect={setSelectedFactor} />;
  }

  let Form = null;

  switch (selectedFactor) {
    case 'email':
      Form = EmailOtpForm;
      break;
    case 'totp':
      Form = TotpForm;
      break;
    case 'sms':
      Form = SmsOtpForm;
      break;
    case 'test':
      throw new Error(
        'For testing menu listing only - not supported for selection'
      );
  }

  return (
    <Form
      onSuccess={handleSuccess}
      onFailure={handleFailure}
      isVerificationRequested={isVerificationRequested}
      onSubmittableChange={handleSubmittableChange}
    />
  );
}

/**
 * If MFA is not enabled, when the modal is opened, immediately dispatch
 * onSuccessMessage
 */
function useMfaDisabledEffect(
  alreadyVerifiedStatus: AlreadyVerifiedState,
  setAlreadyVerifiedStatus: StateSetter<AlreadyVerifiedState>
) {
  const dispatch = useDispatch();
  const { shouldSkipPrompt, isOpen, onSuccessMessage, protectedUrls } =
    useSelector(selectState);

  useEffect(() => {
    doAsyncEffect().catch(() => {});

    async function doAsyncEffect() {
      if (!isOpen) {
        return;
      }

      if (!protectedUrls) {
        dispatch(thunks.requestProtectedUrls());

        return;
      }

      if (
        shouldSkipPrompt ||
        alreadyVerifiedStatus === AlreadyVerifiedState.AlreadyVerified
      ) {
        dispatchVerifications(dispatch, onSuccessMessage);
        setAlreadyVerifiedStatus(AlreadyVerifiedState.Unknown);

        return;
      }

      if (alreadyVerifiedStatus === AlreadyVerifiedState.Unknown) {
        setAlreadyVerifiedStatus(AlreadyVerifiedState.Pending);
        const result = await api.checkAlreadyVerified();
        const alreadyVerifiedStatusResult = result
          ? AlreadyVerifiedState.AlreadyVerified
          : AlreadyVerifiedState.NotAlreadyVerified;

        setAlreadyVerifiedStatus(alreadyVerifiedStatusResult);
      }
    }
  }, [
    shouldSkipPrompt,
    isOpen,
    dispatch,
    onSuccessMessage,
    protectedUrls,
    alreadyVerifiedStatus,
    setAlreadyVerifiedStatus,
  ]);
}

function selectState(state: RootState): SelectedState {
  const isMfaFeatureFlagEnabled = state.user.domainInfo?.FEATURE_MFA === true;
  const {
    isOpen,
    onSuccessMessage,
    requestedUrl,
    requestedMethod,
    protectedUrls,
  } = state.mfaPrompt;
  const isCodeToggleEnabled = !['0', 'false', 'disabled'].includes(
    (
      localStorage.getItem('code-toggle:nav/mfa-prompt') ?? CODE_TOGGLE_DEFAULT
    ).toLowerCase()
  );

  // Assume URL is protected while we wait for the response, so that we don't
  // skip MFA prematurely.
  let isUrlProtected = true;

  if (
    isOpen &&
    protectedUrls &&
    !isEnumMember(protectedUrls, ResponseFailure)
  ) {
    let methodMatchUrls: string[] = [];

    (
      Object.entries(protectedUrls) as [ProtectedUrlMethods, string[]][]
    ).forEach(([key, value]) => {
      if (['ALL', requestedMethod].includes(key)) {
        methodMatchUrls = methodMatchUrls.concat(value);
      }
    });

    isUrlProtected = methodMatchUrls.includes(requestedUrl);
  }

  const shouldSkipPrompt =
    !isUrlProtected || !isMfaFeatureFlagEnabled || !isCodeToggleEnabled;

  return {
    shouldSkipPrompt,
    isOpen,
    requestedMethod,
    protectedUrls,
    onSuccessMessage,
  };
}

function dispatchVerifications(
  dispatch: AppDispatch,
  onSuccessMessage?: Action
) {
  if (onSuccessMessage) {
    (dispatch as Dispatch)(onSuccessMessage);
  }

  dispatch(privateActions.verificationSucceeded());
}

function useModalIslandProps(
  selectedFactor: FactorType | null,
  handleNextClick: () => void,
  handleBackClick: () => void,
  canContinue: boolean
) {
  const availableFactors = useSelector(
    (state) => state.mfaPrompt.availableFactors
  );

  const footerActionBarComp = useFooterActionBarComp(
    selectedFactor,
    handleNextClick,
    handleBackClick,
    canContinue
  );
  const l = useInvertedLocalize(invertedMessages);

  if (selectedFactor) {
    return {
      className: 'Nav_PromptModal Nav_PromptModal--isForm',
      title: l('Enter Your Passcode'),
      subTitle: l(
        'Open your authenticator and enter the passcode to verify your identity.'
      ),
      footerActionBarComp,
    };
  }

  const subTitle = isEnumMember(availableFactors, ResponseFailure)
    ? undefined
    : l('Select one of the methods to continue.');

  return {
    className: 'Nav_PromptModal Nav_PromptModal--isMenu',
    title: l('Verify Your Identity'),
    subTitle,
    footerActionBarComp,
  };
}

function useFooterActionBarComp(
  selectedFactor: FactorType | null,
  handleNextClick: () => void,
  handleBackClick: () => void,
  canContinue: boolean
) {
  const l = useInvertedLocalize(invertedMessages);

  if (selectedFactor) {
    return (
      <FooterActionBar>
        <Box className='Nav_PromptModal--footer'>
          <div className='FooterActionBar-extraContent'>
            <Button kind='subtle' size='medium' onClick={handleBackClick}>
              {l('Back')}
            </Button>
          </div>
          <div className='FooterActionBar-actionButtons'>
            <Button
              kind='filled'
              size='medium'
              onClick={handleNextClick}
              isDisabled={!canContinue}>
              {l('Next')}
            </Button>
          </div>
        </Box>
      </FooterActionBar>
    );
  }

  return undefined;
}
